import type { MatchedProviderApi, MatchedSpecialtyApi } from './navigator-matching.types';

/**
 * Feature 080 — single-select helpers shared by the generation store, the
 * matching pipeline and the view-model builder so FP and CS surfaces apply
 * the exact same selection rule: the `user_selected` provider wins, falling
 * back to the AI top match (rank 1 = first in API order).
 */
export const selectedProviderFor = (
  specialty: MatchedSpecialtyApi,
): MatchedProviderApi | undefined =>
  specialty.providers.find((provider) => provider.user_selected) ?? specialty.providers[0];

/** Selected provider first; the rest keep their AI rank order. */
export const orderProvidersSelectedFirst = (
  providers: MatchedProviderApi[],
): MatchedProviderApi[] => {
  const selectedIndex = providers.findIndex((provider) => provider.user_selected);
  if (selectedIndex <= 0) return providers;
  return [providers[selectedIndex], ...providers.filter((_, index) => index !== selectedIndex)];
};

/** Apply selected-first ordering to every specialty in a categories snapshot. */
export const normalizeCategories = (categories: MatchedSpecialtyApi[]): MatchedSpecialtyApi[] =>
  categories.map((specialty) => ({
    ...specialty,
    providers: orderProvidersSelectedFirst(specialty.providers),
  }));

/**
 * POST /find-providers/care-plan body: exactly one provider id per non-empty
 * specialty. The full map is required — the backend resets every selection
 * for the session before re-marking, so omitting a specialty would wipe it.
 */
export const buildSelectionPayload = (
  categories: MatchedSpecialtyApi[],
): { specialty_id: string; provider_ids: string[] }[] =>
  categories
    .filter((specialty) => specialty.providers.length > 0)
    .map((specialty) => ({
      specialty_id: specialty.specialty_id,
      provider_ids: [selectedProviderFor(specialty)!.provider_id],
    }));
